import { planLib } from "@agentcall/core/lib"
import type { PlanRes } from "@agentcall/core/res"
import { cva, type VariantProps } from "class-variance-authority"
import type * as React from "react"
import { cn } from "@/lib/utils"

const planBadgeVariants = cva(
  "inline-flex items-center rounded-full border border-transparent px-2.5 pt-0.5 leading-1 text-xs font-semibold text-white transition-colors",
  {
    variants: {
      variant: {
        active: "bg-[#25D2AF] hover:bg-[#25D2AF]",
        ending: "bg-[#E6CD0D] hover:bg-[#E6CD0D]",
        planned: "bg-[#458CF7] hover:bg-[#458CF7]",
        none: "bg-slate-200 text-slate-600 hover:bg-slate-200",
      },
    },
    defaultVariants: {
      variant: "active",
    },
  }
)

const PLAN_BADGE_LABELS = {
  currentAndContinue: "プラン利用中",
  currentAndStop: "プラン利用停止予定",
  future: "プラン利用開始予定",
  past: "プラン利用終了済み",
  none: "未利用",
} as const

export type PlanBadgeVariant = VariantProps<typeof planBadgeVariants>["variant"]

export type PlanBadgeInfo = {
  variant: NonNullable<PlanBadgeVariant>
  label: string
} | null

export const getPlanBadgeInfo = (plan: PlanRes | null): PlanBadgeInfo => {
  if (!plan) {
    return { variant: "none", label: PLAN_BADGE_LABELS.none }
  }

  if (planLib.isCurrentAndContinue(plan, null)) {
    return { variant: "active", label: PLAN_BADGE_LABELS.currentAndContinue }
  }

  if (planLib.isCurrentAndStop(plan, null)) {
    return { variant: "ending", label: PLAN_BADGE_LABELS.currentAndStop }
  }

  if (planLib.isFuture(plan, null)) {
    return { variant: "planned", label: PLAN_BADGE_LABELS.future }
  }

  if (planLib.isPast(plan, null)) {
    return { variant: "active", label: PLAN_BADGE_LABELS.past }
  }

  return null
}

export interface PlanBadgeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof planBadgeVariants> {
  plan: PlanRes | null
}

function PlanBadge({ plan, className, ...props }: PlanBadgeProps) {
  const badgeInfo = getPlanBadgeInfo(plan)

  if (!badgeInfo) {
    return null
  }

  return (
    <div className={cn(planBadgeVariants({ variant: badgeInfo.variant }), className)} {...props}>
      {badgeInfo.label}
    </div>
  )
}

export { PlanBadge, planBadgeVariants }
